/**
 * LoadGameScene.ts — Save slot browser with load/delete.
 *
 * Shows 6 save slots (0=auto, 1-5=manual) with metadata.
 * Player can load from a slot or delete a save.
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import { MAX_SAVE_SLOTS } from '@core/engine/SaveLoad';
import { StoryScene } from './StoryScene';
import { formatPlaytime, formatDate } from './CharacterScene';

export class LoadGameScene implements Scene {
  name = 'LoadGameScene';

  async enter(engine: GameEngine): Promise<void> {
    const ctx = engine.getContext();
    const { menu, saveSystem, frameRenderer, effects } = ctx;

    while (true) {
      ctx.sprites.hide();
      const layout = frameRenderer.drawFrame({
        title: 'LOAD GAME',
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();

      const slots = saveSystem.getAllSaveSlots();
      const options: MenuOption[] = slots.map((meta, i) => {
        if (meta) {
          const prefix = i === 0 ? '[Auto]' : `Slot ${i}`;
          const playtime = formatPlaytime(meta.playtime);
          const date = formatDate(meta.savedAt);
          return {
            label: `${prefix} ${meta.characterName}, ${meta.className} Lv.${meta.level}`,
            color: 'white' as const,
            suffix: `Ch.${meta.chapterNumber} | ${playtime} | ${date}`,
            suffixColor: 'frostSilver' as const,
          };
        }
        return {
          label: i === 0 ? '[Auto] Empty' : `Slot ${i}: [Empty]`,
          color: 'gray' as const,
          disabled: true,
        };
      });

      const menuCfg = writer.menuConfig();
      const sel = await menu.show(options, {
        showExit: true,
        exitLabel: 'Back',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: 'LOAD GAME',
            borderColor: 'frostBlue',
          });
        },
      });

      if (sel === -1) {
        await engine.popScene();
        return;
      }

      if (sel >= 0 && sel < MAX_SAVE_SLOTS && slots[sel]) {
        // Show submenu: Load, Delete, Cancel
        await this.slotAction(engine, sel);
      }
    }
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }

  private async slotAction(engine: GameEngine, slot: number): Promise<void> {
    const ctx = engine.getContext();
    const { menu, prompt, effects, saveSystem, storyEngine, events, frameRenderer } = ctx;
    const meta = saveSystem.getSaveMetadata(slot);
    if (!meta) return;

    ctx.sprites.hide();
    const layout = frameRenderer.drawFrame({
      title: `${meta.characterName}`,
      headerRight: `${meta.className} Lv.${meta.level}`,
      borderColor: 'frostBlue',
    });

    const writer = frameRenderer.createWriter(layout.content, effects);
    writer.blankLine();
    writer.writeLine(`  ${meta.characterName}, ${meta.className} Lv.${meta.level}`, 'icyWhite');
    writer.writeLine(`  Chapter ${meta.chapterNumber}: ${meta.chapterName}`, 'frostSilver');
    writer.blankLine();

    const options: MenuOption[] = [
      { label: 'Load', color: 'green' },
      { label: 'Delete', color: 'red' },
    ];

    const menuCfg = writer.menuConfig();
    const sel = await menu.show(options, {
      showExit: true,
      exitLabel: 'Cancel',
      startRow: menuCfg.startRow,
      startCol: menuCfg.startCol,
      maxWidth: menuCfg.maxWidth,
    });

    switch (sel) {
      case 0: { // Load
        const confirmed = await prompt.confirm('Load this save?');
        if (!confirmed) return;

        const state = saveSystem.load(slot);
        if (!state) {
          writer.blankLine();
          writer.writeLine('  Failed to load save data.', 'red');
          await writer.pressAnyKey();
          return;
        }

        state.setEventSystem(events);
        ctx.gameState = state;
        storyEngine.setState(state);

        // Show loading in frame
        ctx.sprites.hide();
        const loadLayout = frameRenderer.drawFrame({
          title: 'LOADING...',
          borderColor: 'frostBlue',
        });
        const loadWriter = frameRenderer.createWriter(loadLayout.content, effects);
        const offset = loadWriter.calculateVerticalOffset(3);
        loadWriter.skip(offset);

        try {
          await effects.withLoading('  Loading...', storyEngine.initialize());
          await engine.replaceScene(new StoryScene());
          return;
        } catch (err) {
          console.error('Failed to load story data:', err);
          loadWriter.blankLine();
          loadWriter.writeLine('  Failed to load story data.', 'red');
          ctx.gameState = null;
          await loadWriter.pressAnyKey();
          return;
        }
      }

      case 1: { // Delete
        const confirmed = await prompt.confirm('Delete this save? This cannot be undone.', false);
        if (confirmed) {
          saveSystem.deleteSave(slot);
          writer.blankLine();
          writer.writeLine('  Save deleted.', 'red');
          await effects.delay(1000);
        }
        break;
      }
    }
  }
}
